import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  EventEmitter,
  OnDestroy,
  Output,
  inject,
} from '@angular/core';

/** The header cell a user resized and by how many pixels. */
export interface ColumnResizeEvent {
  element: HTMLElement;
  delta: number;
}

/**
 * The resize handle at the right edge of a header cell:
 * `<span appColumnResizer (columnResize)="...">` inside the `th`. A line
 * follows the pointer over the table while dragging, and on release the
 * handle reports the header cell and the distance. The table (`.app-table`)
 * holds the line; the click that ends the drag does not reach the header, so
 * it does not sort.
 */
@Directive({
  selector: '[appColumnResizer]',
  standalone: true,
  host: {
    class: 'app-column-resizer',
    '(mousedown)': 'onMouseDown($event)',
    '(click)': '$event.stopPropagation()',
  },
})
export class ColumnResizerDirective implements OnDestroy {
  private element = inject<ElementRef<HTMLElement>>(ElementRef);
  private document = inject(DOCUMENT);

  @Output() columnResize = new EventEmitter<ColumnResizeEvent>();

  private stop?: () => void;

  onMouseDown(event: MouseEvent) {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const header = this.element.nativeElement.parentElement;
    const table = this.element.nativeElement.closest<HTMLElement>('.app-table');
    if (!header || !table) {
      return;
    }
    const helper = this.helper(table);
    const tableLeft = () => table.getBoundingClientRect().left;
    const place = (x: number) => {
      helper.style.left = `${x - tableLeft() + table.scrollLeft}px`;
    };
    const start = event.clientX;
    helper.style.top = '0px';
    helper.style.height = `${table.offsetHeight}px`;
    helper.style.display = 'block';
    place(start);
    table.classList.add('app-unselectable-text');

    const move = (moved: MouseEvent) => place(moved.clientX);
    const up = (released: MouseEvent) => {
      this.stop?.();
      // The click that follows the release would sort the column.
      const swallow = (click: Event) => click.stopPropagation();
      this.document.addEventListener('click', swallow, true);
      setTimeout(() =>
        this.document.removeEventListener('click', swallow, true)
      );
      this.columnResize.emit({
        element: header,
        delta: released.clientX - start,
      });
    };
    this.document.addEventListener('mousemove', move);
    this.document.addEventListener('mouseup', up);
    this.stop = () => {
      this.document.removeEventListener('mousemove', move);
      this.document.removeEventListener('mouseup', up);
      helper.style.display = 'none';
      table.classList.remove('app-unselectable-text');
      this.stop = undefined;
    };
  }

  ngOnDestroy(): void {
    this.stop?.();
  }

  private helper(table: HTMLElement) {
    let helper = table.querySelector<HTMLElement>(
      ':scope > .app-column-resizer-helper'
    );
    if (!helper) {
      helper = this.document.createElement('div');
      helper.className = 'app-column-resizer-helper';
      table.appendChild(helper);
    }
    return helper;
  }
}
